import React from 'react';
import PropTypes from 'prop-types';

/**
 * Card component following UI design specifications
 * @param {Object} props - Component props
 * @returns {React.ReactElement} Card component
 */
const Card = ({
  children,
  variant = 'default',
  padding = 'medium',
  className = '',
  hover = false,
  ...props
}) => {
  // Base styles
  const baseStyles = 'bg-white transition-all duration-300';

  // Variant styles
  const variantStyles = {
    default: 'rounded-2xl shadow-lg border border-gray-100',
    modern: `
      rounded-3xl shadow-xl border border-white/20 
      backdrop-blur-xl bg-white/90
    `,
    glass: `
      rounded-2xl shadow-lg border border-white/30 
      backdrop-blur-md bg-white/70
    `,
    floating: `
      rounded-2xl border border-white/18
      shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]
    `,
    simple: 'rounded-xl shadow-md border border-gray-200'
  };

  // Padding styles
  const paddingStyles = {
    none: '',
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8'
  };

  // Hover styles
  const hoverStyles = hover ? 'hover:shadow-2xl hover:scale-[1.02]' : '';

  // Combine classes
  const combinedClasses = [
    baseStyles,
    variantStyles[variant],
    paddingStyles[padding],
    hoverStyles,
    className
  ].join(' ').replace(/\s+/g, ' ').trim();

  return (
    <div className={combinedClasses} {...props}>
      {children}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'modern', 'glass', 'floating', 'simple']),
  padding: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  className: PropTypes.string,
  hover: PropTypes.bool
};

export default Card; 